import { NavLink } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import { hasArea, isReadOnly, ROLE_LABELS, type PermissionArea } from '../../lib/permissions';
import { useAdvancedModulesHidden } from '../../lib/ui-prefs';
import { Badge } from '../ui';

interface NavItem {
  to: string;
  label: string;
  area: PermissionArea;
  /**
   * ⚠ M11 DENETİMİ: bijberoep bir gerçek kişi için YASAL OLARAK ZORUNLU
   * DEĞİL. "Gelişmiş modülleri gizle" açıkken menüden TAMAMEN düşer; kapalıyken
   * soluk gösterilir (bkz. `useAdvancedModulesHidden`, `AdvancedSection`).
   * Veri/erişim etkilenmez — yalnız menü görünürlüğü.
   */
  advanced?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * Menü.
 *
 * ⚠ Burada YALNIZCA gerçekten var olan rotalar durur. Henüz yazılmamış
 * milestone'ların (fatura, KDV beyanı, Peppol, banka…) bağlantısını şimdiden
 * koymak, tıklandığında boş ekran veren bir menü demektir; bir muhasebe
 * panelinde "burada bir şey var" izlenimi vermek, hiç göstermemekten kötüdür.
 * Yeni bölüm eklendiğinde buraya bir satır eklenir.
 */
const SECTIONS: NavSection[] = [
  {
    title: 'Genel',
    items: [
      { to: '/', label: 'Panel', area: 'dashboard' },
      /*
       * ⚠ ASİSTAN AYRI BİR ALANDIR (`assistant`), `dashboard` DEĞİL: sohbet
       * araç çağırıp taslak üretebiliyor — salt görüntüleme değil.
       */
      { to: '/assistant', label: 'Asistan', area: 'assistant' },
    ],
  },
  {
    title: 'Satış',
    items: [
      { to: '/invoices', label: 'Satış faturaları', area: 'invoices' },
      /*
       * ⚠ ABONELİK ALANI `recurring` — `invoices` DEĞİL. Dönemsel sözleşme
       * yönetmek, tek tek fatura kesmekten ayrı bir sorumluluktur.
       */
      { to: '/subscriptions', label: 'Abonelikler', area: 'recurring' },
      /*
       * ⚠ M17 — snak'ın aylık platform faturaları kendiliğinden kesilir; bu
       * ekran KESİLMEYENLERİ (bekletilen aylar) gösterir. Alan `invoices`.
       */
      { to: '/integrations/snak', label: 'snak faturaları', area: 'invoices' },
      /*
       * ⚠ İŞLER ALANI `projects`. İş kaydı tutmak, fatura kesmekten ayrı bir
       * sorumluluktur ve ayrı verilebilmeli.
       */
      { to: '/projects', label: 'İşler', area: 'projects' },
      { to: '/banking', label: 'Banka', area: 'banking' },
      { to: '/compliance', label: 'Uyum takvimi', area: 'compliance' },
      { to: '/contacts', label: 'Cariler', area: 'contacts' },
      /*
       * Katalog `invoices` alanındadır, `items` diye bir alan yoktur —
       * sunucudaki ItemsController da `@AreaAuth('invoices')` taşır. Cari
       * yetkisi verilip fatura yetkisi verilmemiş bir kullanıcı katalogu
       * görmez; görseydi her isteği 403 dönerdi.
       */
      { to: '/items', label: 'Katalog', area: 'invoices' },
      /*
       * E-fatura AYRI bir yetki alanıdır (`einvoice`), `invoices` değil:
       * gönderim ve gelen belgeler, fatura kesmekten farklı bir sorumluluktur
       * ve ayrı verilebilmeli.
       */
      { to: '/einvoice', label: 'E-fatura (Peppol)', area: 'einvoice' },
    ],
  },
  /*
   * ⚠ BU BÖLÜM BİR SÜRE MENÜDE YOKTU. `/purchases` rotası ve `RequireArea`
   * kapısı M3'te yazıldı ama menüye satır eklenmedi; ekran ancak adres çubuğuna
   * elle yazılarak açılabiliyordu. Yeni bölüm eklenince buraya satır eklemek
   * bir alışkanlık değil, ŞART.
   */
  {
    title: 'Alış',
    items: [
      { to: '/purchases', label: 'Alış faturaları', area: 'purchases' },
      /*
       * ⚠ GİDERLERİN KENDİ ALANI VAR (`expenses`) — ilk yazımda `purchases`
       * altına konmuştu ve yanlıştı: alan M0'dan beri tanımlıydı ve rol
       * varsayılanlarında zaten yer alıyordu.
       *
       * ⚠ AYRI SATIR OLMASI ise ZORUNLU: ayrım belgenin türüdür ve yanlış
       * ekrana giren bir FATURA, KDV indirimini sessizce kaybettirir.
       */
      { to: '/expenses', label: 'Giderler (fişler)', area: 'expenses' },
    ],
  },
  {
    title: 'Defter',
    items: [
      { to: '/accounting', label: 'Yevmiye ve hesap planı', area: 'accounting' },
      { to: '/accounting/trial-balance', label: 'Mizan ve defter-i kebir', area: 'accounting' },
      { to: '/vat-return', label: 'KDV beyanı', area: 'vat-return' },
      /*
       * ⚠ DURAN VARLIK AYRI BİR ALANDIR (`assets`): amortisman kararı, fatura
       * kesmekten de gider girmekten de ayrı bir sorumluluktur.
       */
      { to: '/assets', label: 'Duran varlıklar', area: 'assets', advanced: true },
      /*
       * ⚠ VERGİ TAHMİNİ `reports` ALANINDA — `vat-return` değil. KDV beyanı bir
       * YÜKÜMLÜLÜKTÜR; "ne kadar kenara ayırmalıyım" ise salt okunur bir
       * tahmindir ve görmesi gereken kişi onu vermesi gereken kişi olmayabilir.
       */
      { to: '/reports', label: 'Ne kadar kenara ayırmalıyım?', area: 'reports' },
    ],
  },
  {
    title: 'Yönetim',
    items: [
      { to: '/settings', label: 'Ayarlar', area: 'settings' },
      { to: '/users', label: 'Kullanıcılar', area: 'users' },
      { to: '/audit-log', label: 'Denetim kaydı', area: 'audit-log' },
    ],
  },
];

export function Sidebar() {
  const { user, signOut } = useAuth();
  const advancedHidden = useAdvancedModulesHidden();
  if (!user) return null;

  // Yetkisi olmayan öğe düşer; "gelişmiş" öğe yalnız tercih AÇIKKEN düşer.
  // Boş kalan bölüm de düşer.
  const sections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => hasArea(user, i.area) && !(i.advanced === true && advancedHidden)),
  })).filter((s) => s.items.length > 0);

  return (
    <aside className="flex w-56 shrink-0 flex-col border-r border-[var(--c-border)] bg-[var(--c-surface)]">
      <div className="border-b border-[var(--c-border)] px-4 py-4">
        <p className="text-sm font-semibold tracking-tight">CentraMir</p>
        <p className="text-[11px] text-[var(--c-ink-faint)]">Muhasebe &amp; faturalama</p>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section) => (
          <div key={section.title} className="mb-4">
            <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--c-ink-faint)]">
              {section.title}
            </p>
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                title={item.advanced ? "Gelişmiş — Ayarlar → Görünüm'den gizlenebilir" : undefined}
                className={({ isActive }) =>
                  [
                    'block rounded-[var(--r-md)] px-2 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-[var(--c-brand-soft)] font-medium text-[var(--c-brand)]'
                      : item.advanced
                        ? 'text-[var(--c-ink-faint)] hover:bg-[var(--c-surface-alt)] hover:text-[var(--c-ink-soft)]'
                        : 'text-[var(--c-ink-soft)] hover:bg-[var(--c-surface-alt)]',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-[var(--c-border)] px-4 py-3">
        <NavLink to="/profile" className="block text-sm font-medium hover:underline">
          {user.name}
        </NavLink>
        <div className="mt-1 flex items-center gap-1.5">
          <Badge tone="brand">{ROLE_LABELS[user.role]}</Badge>
          {isReadOnly(user) && <Badge tone="warn">salt okuma</Badge>}
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-2 text-xs text-[var(--c-ink-soft)] hover:text-[var(--c-danger)] hover:underline"
        >
          Çıkış yap
        </button>
      </div>
    </aside>
  );
}
